// Small original line-icon set (24x24, stroke-based) — no external icon
// library. Names match CategoryDefinition.icon (shared/rules-engine/
// categories.ts), which has existed since the tile picker was first built
// but was never actually rendered until now, plus a few general-purpose
// icons used around the wizard.
export type IconName =
  | "mail"
  | "shirt"
  | "cookie"
  | "thermometer"
  | "cpu"
  | "pill"
  | "spray"
  | "wine"
  | "gem"
  | "wine-glass"
  | "scissors"
  | "alert-triangle"
  | "package"
  | "check-circle"
  | "pause-circle"
  | "truck"
  | "coin"
  | "zap"
  | "star"
  | "shield-check"
  | "map-pin"
  | "home"
  | "settings"
  | "sliders"
  | "printer"
  | "printer-off"
  | "clock";

interface Props {
  name: IconName;
  size?: number;
  strokeWidth?: number;
}

const PATHS: Record<IconName, string> = {
  mail: '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/>',
  shirt: '<path d="M8 4 4 8l3 3 2-2v10h6V9l2 2 3-3-4-4-2 2h-2z"/>',
  cookie:
    '<circle cx="12" cy="12" r="9"/><circle cx="9" cy="10" r="1" fill="currentColor" stroke="none"/><circle cx="14" cy="9" r="1" fill="currentColor" stroke="none"/><circle cx="15" cy="14" r="1" fill="currentColor" stroke="none"/><circle cx="10" cy="15" r="1" fill="currentColor" stroke="none"/>',
  thermometer:
    '<path d="M12 3a2 2 0 0 1 2 2v8.5a3.5 3.5 0 1 1-4 0V5a2 2 0 0 1 2-2z"/><circle cx="12" cy="17" r="1" fill="currentColor" stroke="none"/>',
  cpu: '<rect x="6" y="6" width="12" height="12" rx="2"/><rect x="9" y="9" width="6" height="6"/><path d="M9 2v3M15 2v3M9 19v3M15 19v3M2 9h3M2 15h3M19 9h3M19 15h3"/>',
  pill: '<rect x="2" y="9" width="20" height="6" rx="3"/><line x1="12" y1="9" x2="12" y2="15"/>',
  spray: '<rect x="9" y="9" width="6" height="12" rx="1.5"/><rect x="10" y="5" width="4" height="4"/><path d="M14 6h2M15 4l1.5-1M15 8l1.5 1"/>',
  wine: '<path d="M10 2h4v4l2 3v11a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2V9l2-3V2z"/>',
  gem: '<path d="M6 9L9 3h6l3 6-9 12z"/><path d="M6 9h12M9 3l3 6 3-6"/>',
  "wine-glass": '<path d="M7 3h10l-1 7a4 4 0 0 1-8 0z"/><path d="M12 14v6M8 20h8"/>',
  scissors: '<circle cx="6" cy="6" r="2.5"/><circle cx="6" cy="18" r="2.5"/><path d="M8.5 7.5L20 19M8.5 16.5L20 5"/>',
  "alert-triangle": '<path d="M12 3l10 18H2z"/><path d="M12 9v5"/><circle cx="12" cy="17" r="1" fill="currentColor" stroke="none"/>',
  package: '<path d="M12 2l9 5v10l-9 5-9-5V7z"/><path d="M3 7l9 5 9-5M12 12v9"/>',
  "check-circle": '<circle cx="12" cy="12" r="9"/><path d="M8 12l3 3 5-6"/>',
  "pause-circle": '<circle cx="12" cy="12" r="9"/><path d="M10 9v6M14 9v6"/>',
  truck: '<rect x="1" y="7" width="13" height="10" rx="1"/><path d="M14 10h4l3 3v4h-7z"/><circle cx="6" cy="18" r="1.6"/><circle cx="17.5" cy="18" r="1.6"/>',
  coin: '<circle cx="12" cy="12" r="9"/><path d="M12 7v10"/><path d="M9.5 9.3c0-1 1.1-1.6 2.5-1.6s2.5.6 2.5 1.5-1 1.2-2.5 1.6-2.5.7-2.5 1.7 1.1 1.5 2.5 1.5 2.5-.5 2.5-1.5"/>',
  zap: '<path d="M13 2 4 14h6l-1 8 9-12h-6z"/>',
  star: '<path d="M12 2l2.9 6.4 7 0.7-5.3 4.7 1.6 6.9L12 17l-6.2 3.7 1.6-6.9L2 9.1l7-0.7z"/>',
  "shield-check": '<path d="M12 3l7 3v6c0 5-3 8-7 9-4-1-7-4-7-9V6z"/><path d="M9 12l2 2 4-4"/>',
  "map-pin": '<path d="M12 21s7-6.5 7-11.5A7 7 0 0 0 5 9.5C5 14.5 12 21 12 21z"/><circle cx="12" cy="9.5" r="2.3"/>',
  home: '<path d="M3 11l9-8 9 8"/><path d="M5 10v10h14V10"/><path d="M9 20v-6h6v6"/>',
  settings:
    '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6v.2h-4V21a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9A1.7 1.7 0 0 0 3 14H2.8v-4H3a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7 7 4.2l.1.1A1.7 1.7 0 0 0 9 4.6 1.7 1.7 0 0 0 10 3v-.2h4V3a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.2v4H21a1.7 1.7 0 0 0-1.6 1z"/>',
  sliders:
    '<line x1="4" y1="6" x2="12" y2="6"/><line x1="16" y1="6" x2="20" y2="6"/><circle cx="14" cy="6" r="2"/><line x1="4" y1="12" x2="6" y2="12"/><line x1="10" y1="12" x2="20" y2="12"/><circle cx="8" cy="12" r="2"/><line x1="4" y1="18" x2="15" y2="18"/><line x1="19" y1="18" x2="20" y2="18"/><circle cx="17" cy="18" r="2"/>',
  printer: '<path d="M6 9V3h12v6"/><rect x="4" y="9" width="16" height="8" rx="1.5"/><path d="M6 14h12v7H6z"/>',
  "printer-off": '<path d="M6 9V3h12v3"/><rect x="4" y="9" width="16" height="8" rx="1.5"/><path d="M6 14h12v7H6z"/><line x1="2" y1="2" x2="22" y2="22"/>',
  clock: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/>',
};

export default function Icon({ name, size = 22, strokeWidth = 1.8 }: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: PATHS[name] }}
    />
  );
}
